import { Badge, Button, Group, Modal, Select, Stack, Text, Textarea } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { useState } from "react";
import { RecordDrawer } from "@how/ui";
import {
  CARE_STATUS_COLOR,
  CARE_STATUS_LABEL,
  useAssignCare,
  useChurchLogins,
  useSetCareStatus,
  type CareRow,
} from "./care-queries";
import { semanticColor } from "@how/design-system";

export interface CareDrawerProps {
  readonly request: CareRow | null;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

/** One request: the words, who and when, and the four things a carer does with it. */
export function CareDrawer({ request: r, canManage, onClose }: CareDrawerProps) {
  const assign = useAssignCare();
  const setStatus = useSetCareStatus();
  const [closing, setClosing] = useState(false);
  const [handing, setHanding] = useState(false);
  return (
    <>
      <RecordDrawer
        opened={r !== null}
        onClose={onClose}
        title={r === null ? "" : `${r.firstName} ${r.lastName}`}
      >
        {r === null ? null : (
          <Stack gap="md" mt="md">
            <Group gap="xs">
              <Badge variant="light" color={CARE_STATUS_COLOR[r.status]}>
                {CARE_STATUS_LABEL[r.status]}
              </Badge>
              {r.confidential ? (
                <Badge
                  variant="light"
                  color={semanticColor("special")}
                  leftSection={<IconLock size={10} />}
                >
                  Confidential
                </Badge>
              ) : null}
              <Text size="xs" c="dimmed">
                {new Date(r.createdAt).toLocaleString(undefined, {
                  day: "numeric",
                  month: "short",
                  hour: "numeric",
                  minute: "2-digit",
                })}
                {" · "}
                {r.source === "member_portal" ? "asked in the portal" : "logged by staff"}
                {r.household === null ? "" : ` · ${r.household}`}
              </Text>
            </Group>
            <Text style={{ whiteSpace: "pre-wrap" }}>{r.body}</Text>
            <Text size="sm" c="dimmed">
              {r.mayContact ? "Happy to be contacted." : "Asked for prayer only; no contact."}
              {r.ownerName === null
                ? r.ownerUserId === null
                  ? " Nobody has it yet."
                  : " Someone has it."
                : ` ${r.ownerName} has it.`}
            </Text>
            {r.status === "closed" ? (
              <Text size="sm">
                Closed{" "}
                {r.closedAt === null
                  ? ""
                  : new Date(r.closedAt).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                    })}
                {r.closedNote === null ? "." : `: ${r.closedNote}`}
              </Text>
            ) : null}
            {canManage ? (
              <Group gap="sm" wrap="wrap">
                {r.status === "closed" ? (
                  <Button
                    variant="light"
                    loading={setStatus.isPending}
                    onClick={() => {
                      setStatus.mutate({
                        requestId: r.id,
                        personId: r.personId,
                        status: "in_progress",
                      });
                    }}
                  >
                    Reopen
                  </Button>
                ) : (
                  <>
                    {r.ownerUserId === null ? (
                      <Button
                        loading={assign.isPending}
                        onClick={() => {
                          assign.mutate({ requestId: r.id, ownerUserId: null });
                        }}
                      >
                        Take it
                      </Button>
                    ) : null}
                    <Button
                      variant="light"
                      onClick={() => {
                        setHanding(true);
                      }}
                    >
                      Hand on
                    </Button>
                    {r.status !== "in_progress" ? (
                      <Button
                        variant="light"
                        loading={setStatus.isPending}
                        onClick={() => {
                          setStatus.mutate({
                            requestId: r.id,
                            personId: r.personId,
                            status: "in_progress",
                          });
                        }}
                      >
                        In progress
                      </Button>
                    ) : null}
                    <Button
                      variant="light"
                      color="gray"
                      onClick={() => {
                        setClosing(true);
                      }}
                    >
                      {/* Not "Close": the drawer's ✕ is named Close (theme, #271), and this closes the request. */}
                      Close request…
                    </Button>
                  </>
                )}
              </Group>
            ) : null}
          </Stack>
        )}
      </RecordDrawer>
      <CloseModal
        key={closing ? "open" : "closed"}
        opened={closing && r !== null}
        loading={setStatus.isPending}
        onClose={() => {
          setClosing(false);
        }}
        onConfirm={(note) => {
          if (r === null) return;
          setStatus.mutate(
            { requestId: r.id, personId: r.personId, status: "closed", note },
            {
              onSuccess: () => {
                setClosing(false);
              },
            },
          );
        }}
      />
      <HandOnModal
        key={handing ? "open" : "closed"}
        opened={handing && r !== null}
        loading={assign.isPending}
        onClose={() => {
          setHanding(false);
        }}
        onConfirm={(userId) => {
          if (r === null) return;
          assign.mutate(
            { requestId: r.id, ownerUserId: userId },
            {
              onSuccess: () => {
                setHanding(false);
              },
            },
          );
        }}
      />
    </>
  );
}

function CloseModal({
  opened,
  loading,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Modal opened={opened} onClose={onClose} title="Close this request" radius="lg">
      <Stack gap="sm">
        <Textarea
          label="What was done"
          placeholder="Visited on Tuesday; meal train set up."
          autosize
          minRows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => {
            setNote(e.currentTarget.value);
          }}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={() => {
              onConfirm(note);
            }}
          >
            Close request
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function HandOnModal({
  opened,
  loading,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (userId: string) => void;
}) {
  const logins = useChurchLogins();
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <Modal opened={opened} onClose={onClose} title="Hand this on" radius="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {logins.isError
            ? "You cannot see the church's logins; ask an administrator to hand it on."
            : "Choose who looks after it. They need care.manage to do anything with it."}
        </Text>
        <Select
          label="To"
          placeholder="Choose a login"
          searchable
          data={(logins.data ?? []).map((l) => ({ value: l.userId, label: l.email }))}
          value={userId}
          onChange={setUserId}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={userId === null}
            loading={loading}
            onClick={() => {
              if (userId !== null) onConfirm(userId);
            }}
          >
            Hand on
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
